import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useUser } from "@clerk/clerk-react";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  Globe,
  Layers,
  Loader2,
  Mail,
  Megaphone,
  Search,
  SearchX,
  Sparkles,
  X,
} from "lucide-react";
import { toast } from "sonner";
import {
  raiseBoardApi,
  type RaiseBoardBySlugResponse,
} from "@/api/raise-board-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getErrorMessage } from "@/lib/api-error";
import { GICSSubIndustries } from "@/lib/utils";
import { MultiSelect } from "@/components/ui/multi-select";
import { buildFilterOptions } from "./board-filter-options";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { BoardAuthDialog } from "@/components/auth/board-auth-dialog";
import { SubscribeButton } from "./subscribe-button";
import { BoardCard, BoardList, BoardRow, BoardRowSkeleton } from "./board-row";
import { BoardGrid, ViewToggle, useViewMode } from "./board-collection";
import {
  CoverEyebrow,
  PageContainer,
  PublicCover,
  fundCover,
  PublicState,
  RaiseBoardNav,
  RaiseBoardShell,
  SectionHeading,
} from "./rb-ui";

/*
 * /rb/:slug: one fund's public Raiseboard.
 *
 * The fund owns the page: its mark leads the nav, its name is the headline.
 * Every browse control runs server-side (a board can hold thousands of deals),
 * and a row opens the deal's own profile, where Add to pipeline and Email me
 * this live. Emailing a deal's details stays one click from the list too.
 */

type Application =
  RaiseBoardBySlugResponse["result"]["applications"][number];

/**
 * Rows per page. Boards range from a handful of deals to the YC Alumni board's
 * ~6,200 — at which point the unpaginated payload was ~8.5MB, so the page asks
 * the API for a slice rather than filtering the whole board in the browser.
 */
const PAGE_SIZE = 25;


function getCompany(application: Application) {
  const ac = application.applicant_company;
  if (!ac || typeof ac === "string") return null;
  return ac;
}

// Build a readable industries string from the deal's GICS sub-industry codes,
// resolved to names here on the FE since the GICS taxonomy lives client-side;
// falls back to the plain industry array.
function resolveIndustries(application: Application): string {
  const company = getCompany(application);
  const subIndustriesField = application.deal_info?.find(
    (f) => f.field_name === "sub_industries" && f.field_state === "approved"
  );
  if (subIndustriesField?.field_value) {
    const codes = Array.isArray(subIndustriesField.field_value)
      ? subIndustriesField.field_value
      : [subIndustriesField.field_value as string];
    const names = codes
      .map((c) => GICSSubIndustries.find((si) => si.code === String(c))?.name)
      .filter((n): n is string => !!n);
    if (names.length > 0) return names.join(", ");
  }
  const fallback = application.industry ?? company?.industry ?? [];
  return fallback.filter(Boolean).join(", ");
}

/**
 * The fund's mark: the logo the API attaches when it has one, otherwise the
 * avatar file on the fund's own company record.
 */
function fundLogo(
  opportunity: RaiseBoardBySlugResponse["result"]["opportunity"] | undefined
): string | undefined {
  if (!opportunity) return undefined;
  const direct = (opportunity as { logo_url?: string | null }).logo_url;
  if (direct) return direct;
  return opportunity.affiliated_company?.files?.find(
    (file) => file.file_type === "company avatar"
  )?.file_url;
}

/** "3 days ago", for the cover's one line about how fresh the board is. */
function timeAgo(iso?: string): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days < 1) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} ${months === 1 ? "month" : "months"} ago`;
  const years = Math.floor(months / 12);
  return `${years} ${years === 1 ? "year" : "years"} ago`;
}


export default function RaiseBoardPublic() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const [emailing, setEmailing] = useState<Application | null>(null);
  const [view, setView] = useViewMode("fund-board", "grid");
  const [authReason, setAuthReason] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState<string[]>([]);
  const [industryFilter, setIndustryFilter] = useState<string[]>([]);
  const [countryFilter, setCountryFilter] = useState<string[]>([]);
  const [page, setPage] = useState(1);

  // Search trails the input so typing doesn't fire a request per keystroke.
  const debouncedSearch = useDebouncedValue(search, 300);

  // Any change to what's being asked for puts you back on page one; staying on
  // page 7 of a result set that just shrank to two pages shows nothing.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, stageFilter, industryFilter, countryFilter]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [
      "raise-board",
      slug,
      { page, debouncedSearch, stageFilter, industryFilter, countryFilter },
    ],
    queryFn: () => {
      if (!slug) throw new Error("Missing slug");
      return raiseBoardApi.getBySlug(slug, {
        page,
        limit: PAGE_SIZE,
        search: debouncedSearch,
        stage: stageFilter,
        industry: industryFilter,
        country: countryFilter,
      });
    },
    enabled: !!slug,
    retry: false,
    // Hold the previous page on screen while the next one loads, so paging
    // doesn't collapse the layout to skeletons on every click.
    placeholderData: keepPreviousData,
  });

  // The board's size with nothing applied, remembered so the cover keeps
  // describing the whole board while a search narrows the list below it.
  const [unfilteredTotal, setUnfilteredTotal] = useState(0);
  const anyFilter =
    !!debouncedSearch ||
    stageFilter.length > 0 ||
    industryFilter.length > 0 ||
    countryFilter.length > 0;
  useEffect(() => {
    if (!data?.result || anyFilter) return;
    setUnfilteredTotal(
      data.result.pagination?.total ?? data.result.applications.length
    );
  }, [data, anyFilter]);

  // Computed here rather than after the loading guards because hooks can't sit
  // below an early return, and held on the previous value while a page loads so
  // the dropdowns don't flicker empty mid-browse.
  const filterOptions = useMemo(
    () => buildFilterOptions(data?.result?.facets),
    [data]
  );

  const sendEmail = useMutation({
    mutationFn: async (vars: { application: Application; email: string }) => {
      if (!slug) throw new Error("Missing slug");
      return raiseBoardApi.sendDealEmail({
        slug,
        applicationId: String(vars.application._id),
        recipientEmail: vars.email,
        industries: resolveIndustries(vars.application),
      });
    },
    onSuccess: (_data, vars) => {
      toast.success(`Deal details sent to ${vars.email}.`);
      setEmailing(null);
    },
    onError: (e: unknown) => {
      toast.error(getErrorMessage(e, "Failed to send deal details"));
    },
  });

  const authDialog = (
    <BoardAuthDialog
      open={!!authReason}
      onOpenChange={(open) => {
        if (!open) setAuthReason(null);
      }}
      reason={authReason ?? undefined}
    />
  );

  const unavailable = (
    <RaiseBoardShell>
      <RaiseBoardNav onRequestAuth={setAuthReason} />
      <PublicCover
        size="lg"
        eyebrow={
          <>
            <Megaphone />
            Raiseboard
          </>
        }
        title="This Raiseboard isn't available"
        lede="The link may be wrong, or the fund has switched its board off."
      />
      {authDialog}
    </RaiseBoardShell>
  );

  if (!slug) return unavailable;

  if (isLoading) {
    return (
      <RaiseBoardShell>
        <RaiseBoardNav onRequestAuth={setAuthReason} />
        <div role="status" aria-label="Loading the board">
          <PublicCover
            eyebrow={<Skeleton className="h-3.5 w-44" />}
            title={<Skeleton className="h-[0.9em] w-[8ch] rounded-lg" />}
            lede={<Skeleton className="h-5 w-72" />}
          />
          <PageContainer className="pt-10 sm:pt-12">
            <Skeleton className="h-10 w-56" />
            <BoardList className="mt-6">
              {[0, 1, 2, 3, 4].map((i) => (
                <BoardRowSkeleton key={i} />
              ))}
            </BoardList>
          </PageContainer>
        </div>
        {authDialog}
      </RaiseBoardShell>
    );
  }

  if (error || !data?.result) return unavailable;

  // Facet options are read in `filterOptions` above, which has to run before
  // the loading guards, so they aren't destructured here.
  const { opportunity, applications, pagination } = data.result;
  const fundName = opportunity?.opportunity_name ?? "Raiseboard";

  const totalMatching = pagination?.total ?? applications.length;
  const totalPages = pagination?.total_pages ?? 1;
  const hasFilters =
    !!debouncedSearch ||
    stageFilter.length > 0 ||
    industryFilter.length > 0 ||
    countryFilter.length > 0;

  const clearFilters = () => {
    setSearch("");
    setStageFilter([]);
    setIndustryFilter([]);
    setCountryFilter([]);
  };

  const openDeal = (application: Application) => {
    const company = getCompany(application);
    const companyId =
      typeof application.applicant_company === "string"
        ? application.applicant_company
        : company?._id;
    if (!companyId) {
      toast.error("This deal's profile isn't available.");
      return;
    }
    navigate(`/rb/${slug}/${companyId}`);
  };

  // The cover speaks for the whole board, not the current search, so it holds
  // the unfiltered total once it has seen one.
  const boardTotal = hasFilters ? unfilteredTotal || totalMatching : totalMatching;
  const newest = hasFilters || page !== 1
    ? null
    : timeAgo((applications[0] as { created_on?: string } | undefined)?.created_on);

  return (
    <TooltipProvider delayDuration={200}>
      <RaiseBoardShell footerNote={`${fundName} Raiseboard`}>
        <RaiseBoardNav
          background={fundCover(opportunity)}
          onRequestAuth={setAuthReason}
          brand={{
            name: fundName,
            logoUrl: fundLogo(opportunity),
            href: `/rb/${slug}`,
            context: "Raiseboard",
          }}
        />

        <PublicCover
          background={fundCover(opportunity)}
          eyebrow={
            <CoverEyebrow
              items={[
                "Raiseboard",
                <span className="inline-flex items-center gap-2 normal-case tracking-normal">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inset-0 rounded-full bg-raising/40 motion-safe:animate-ping [animation-duration:2.4s]" />
                    <span className="relative h-1.5 w-1.5 rounded-full bg-raising" />
                  </span>
                  <span className="text-[13px] tracking-[0.02em]">
                    {boardTotal} raising now
                  </span>
                </span>,
              ]}
            />
          }
          title={fundName}
          lede={
            <>
              <strong>
                {boardTotal} {boardTotal === 1 ? "company" : "companies"}
              </strong>{" "}
              raising now, published by {fundName} for the investors who follow
              them.
              {newest && ` The newest arrived ${newest}.`}
            </>
          }
          aside={
            <SubscribeButton
              slug={slug}
              onRequestAuth={setAuthReason}
              className="h-10 rounded-lg px-4 text-[13.5px] [&_svg]:h-3.5 [&_svg]:w-3.5"
            />
          }
        />

        <PageContainer className="pt-10 sm:pt-12">
          <section aria-label="Companies">
            <SectionHeading
              title="The board"
              count={totalMatching}
              actions={
                <div className="relative w-full sm:w-72">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search companies"
                    aria-label="Search companies"
                    className="h-9 rounded-lg border-border bg-card pl-8 pr-8 text-[13px]"
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={() => setSearch("")}
                      aria-label="Clear search"
                      className="absolute right-2 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              }
            />

            {/* Every browse control runs server-side, so the page never holds
                the whole board. */}
            <div className="flex flex-wrap items-center justify-between gap-3 pb-5">
              <div className="-mx-5 flex w-[calc(100%+2.5rem)] items-center gap-2 overflow-x-auto px-5 pb-1 [scrollbar-width:none] sm:mx-0 sm:w-auto sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0 [&>*]:shrink-0">
                <MultiSelect
                  id="board-industry"
                  options={filterOptions.industries}
                  value={industryFilter}
                  onValueChange={setIndustryFilter}
                  placeholder="Industry"
                  triggerIcon={Building2}
                  maxCount={1}
                  className="h-9 w-auto min-w-[140px] rounded-lg border-border bg-card text-[13px]"
                />
                <MultiSelect
                  id="board-stage"
                  options={filterOptions.stages}
                  value={stageFilter}
                  onValueChange={setStageFilter}
                  placeholder="Stage"
                  triggerIcon={Layers}
                  maxCount={2}
                  className="h-9 w-auto min-w-[120px] rounded-lg border-border bg-card text-[13px]"
                />
                <MultiSelect
                  id="board-country"
                  options={filterOptions.countries}
                  value={countryFilter}
                  onValueChange={setCountryFilter}
                  placeholder="Location"
                  triggerIcon={Globe}
                  maxCount={2}
                  className="h-9 w-auto min-w-[130px] rounded-lg border-border bg-card text-[13px]"
                />
                {hasFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="h-9 rounded-lg px-3 text-[13px] text-muted-foreground"
                  >
                    Clear
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-3 text-[13px] tabular-nums text-muted-foreground">
                {isFetching && (
                  <Loader2
                    className="h-3.5 w-3.5 animate-spin"
                    aria-label="Loading results"
                  />
                )}
                {hasFilters ? "Matching your search" : "Newest first"}
                <ViewToggle value={view} onChange={setView} />
              </div>
            </div>

            {applications.length === 0 ? (
              hasFilters ? (
                <PublicState
                  icon={<SearchX />}
                  title="No companies match these filters"
                  className="border-t border-foreground"
                  action={
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-9 rounded-lg bg-card"
                      onClick={clearFilters}
                    >
                      Clear filters
                    </Button>
                  }
                />
              ) : (
                <PublicState
                  icon={<Sparkles />}
                  title="No companies on this board yet"
                  className="border-t border-foreground"
                >
                  {fundName} hasn&rsquo;t published any deals yet. Subscribe
                  and you&rsquo;ll hear the moment they do.
                </PublicState>
              )
            ) : (
              <>
                {view === "grid" ? (
                  <BoardGrid>
                    {applications.map((application, index) => (
                      <BoardCard
                        key={String(application._id)}
                        application={application}
                        index={index}
                        rank={(page - 1) * PAGE_SIZE + index + 1}
                        showSource={false}
                        onOpen={() => openDeal(application)}
                        trailing={<EmailAction onEmail={() => setEmailing(application)} />}
                      />
                    ))}
                  </BoardGrid>
                ) : (
                  <BoardList>
                    {applications.map((application, index) => (
                      <BoardRow
                        key={String(application._id)}
                        application={application}
                        index={index}
                        rank={(page - 1) * PAGE_SIZE + index + 1}
                        showSource={false}
                        onOpen={() => openDeal(application)}
                        trailing={<EmailAction onEmail={() => setEmailing(application)} />}
                      />
                    ))}
                  </BoardList>
                )}

                {totalPages > 1 && (
                  <nav
                    aria-label="Board pages"
                    className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <p className="text-[13px] text-muted-foreground">
                      Showing{" "}
                      <span className="tabular-nums text-foreground">
                        {(page - 1) * PAGE_SIZE + 1}–
                        {Math.min(page * PAGE_SIZE, totalMatching)}
                      </span>{" "}
                      of{" "}
                      <span className="tabular-nums text-foreground">
                        {totalMatching}
                      </span>
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9 rounded-lg bg-card px-3 text-[13px]"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page <= 1}
                      >
                        <ChevronLeft className="h-3.5 w-3.5" />
                        Previous
                      </Button>
                      <span className="px-1 text-[13px] tabular-nums text-muted-foreground">
                        {page} / {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9 rounded-lg bg-card px-3 text-[13px]"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages}
                      >
                        Next
                        <ChevronRight className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </nav>
                )}
              </>
            )}
          </section>
        </PageContainer>

        <SendEmailDialog
          application={emailing}
          defaultEmail={user?.primaryEmailAddress?.emailAddress ?? ""}
          isSending={sendEmail.isPending}
          onSend={(email) => {
            if (emailing) sendEmail.mutate({ application: emailing, email });
          }}
          onClose={() => setEmailing(null)}
        />

        {authDialog}
      </RaiseBoardShell>
    </TooltipProvider>
  );
}

/** "Email me this" for one deal, sized to sit where the upvote would. */
function EmailAction({ onEmail }: { onEmail: () => void }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            onEmail();
          }}
          aria-label="Send details via email"
          className="h-12 w-11 rounded-lg bg-card text-muted-foreground hover:border-foreground/30 hover:text-foreground"
        >
          <Mail className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Email these details</TooltipContent>
    </Tooltip>
  );
}

function SendEmailDialog({
  application,
  defaultEmail,
  isSending,
  onSend,
  onClose,
}: {
  application: Application | null;
  defaultEmail: string;
  isSending: boolean;
  onSend: (email: string) => void;
  onClose: () => void;
}) {
  const [email, setEmail] = useState(defaultEmail);
  useEffect(() => {
    setEmail(defaultEmail);
  }, [defaultEmail, application]);
  if (!application) return null;
  const company = getCompany(application);
  const name = company?.company_name ?? "this company";
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  return (
    <Dialog open={!!application} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogTitle className="text-lg font-semibold tracking-tight">
          Email deal details
        </DialogTitle>
        <p className="text-sm text-muted-foreground">
          Send <span className="font-medium text-foreground">{name}</span>'s details to:
        </p>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@example.com"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter" && valid && !isSending) onSend(email.trim());
          }}
        />
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isSending}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => onSend(email.trim())} disabled={!valid || isSending}>
            {isSending ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Mail className="mr-1.5 h-4 w-4" />
            )}
            Send
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
